import styled, { css } from 'styled-components';
import Color from '../../../styles/Color';
import Theme from '@styles/Theme';

export const buttonSizeMap = {
  xl: css`
    height: 46px;
    font-size: 16px;
    font-weight: 700;
    line-height: 130%;
    padding: 0 24px;
  `,
  lg: css`
    padding: 16px 24px;
    font-size: 16px;
    font-weight: 700;
    line-height: 130%;

    @media (max-width: 768px) {
      font-size: 15px;
      padding: 12px 16px;
    }
  `,
  md: css`
    height: 40px;
    padding: 0 16px;
    font-size: 14px;
    font-weight: 700;
    line-height: 150%;
  `,
  sm: css`
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 600;
    line-height: 140%;
    border-radius: 4px;
  `,
};

export const buttonShapeMap = {
  filled: {
    default: css`
      color: var(--buttonTextColor);
      background: var(--buttonBackgroundColor);
      :hover {
      }
      :disabled {
        opacity: 0.8;
      }
    `,
    primary: css`
      color: var(--subColor);
      background: var(--primaryColor);
      :hover {
      }
      :disabled {
        opacity: 0.8;
      }
    `,
    warning: css`
      color: ${Color.WHITE};
      background: ${Color.RED_6};
      :hover {
        background: ${Color.RED_7};
      }
      :disabled {
        color: ${Color.GRAY_6};
      }
    `,
  },
  stroke: {
    default: css`
      color: var(--textColor);
      background: var(--cardBackgroundColor);
      border: 1px solid var(--lineColor);
      :hover {
        background: var(--inkColor);
      }
      :disabled {
        opacity: 0.6;
      }
    `,
    primary: css``,
    warning: css`
      border: 1px solid ${Color.RED_6};
      color: ${Color.RED_8};
      background: var(--cardBackgroundColor);
      :hover {
        background: ${Color.RED_2};
      }
      :disabled {
        color: ${Color.GRAY_6};
      }
    `,
  },
  ghost: {
    default: css`
      color: var(--textColor);
      background: transparent;
      :hover {
        background: rgba(0, 0, 0, 0.1);
      }
      :disabled {
        opacity: 0.8;
      }
    `,
    primary: css``,
    warning: css`
      border: 0;
      color: ${Color.RED_8};
      background: transparent;
      :hover {
        background: ${Color.RED_2};
      }
      :disabled {
        color: ${Color.GRAY_6};
      }
    `,
  },
};

export const Ink = styled.span`
  background: var(--inkColor);
  display: block;
  position: absolute;
  opacity: 1;
  transform: scale(0);
  border-radius: 50%;
  will-change: transform;
  transform-origin: 50% 50%;
  transition: opacity 0.25s ease-in;

  &.animate {
    animation: ripple 0.4s cubic-bezier(0.75, 0, 0.55, 0.55);
  }

  @keyframes ripple {
    100% {
      opacity: 0;
      transform: scale(2);
    }
  }
`;

export const ButtonBase = styled.button<{
  size: keyof typeof buttonSizeMap;
  shape: keyof typeof buttonShapeMap;
  color?: 'default' | 'primary' | 'warning';
  block?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  box-shadow: none;
  white-space: nowrap;
  color: white;
  transition: 0.2s;
  border: none;
  width: ${({ block }) => block && '100%'};
  font-family: var(--defaultFontFamily);
  position: relative;
  gap: 2px;
  backdrop-filter: blur(8px);
  overflow: hidden;

  &:hover {
    cursor: var(--hoverCursorURL), pointer;
  }

  &:active {
    transform: scale(0.97);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${({ size }) => buttonSizeMap[size]};
  ${({ shape, color }) =>
    shape && color
      ? buttonShapeMap[shape][color]
      : buttonShapeMap['filled']['default']};
`;

export const CustomButtonBase = styled.div<{
  size: keyof typeof buttonSizeMap;
  shape: keyof typeof buttonShapeMap;
  color?: 'default' | 'primary' | 'warning';
  block?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  box-shadow: none;
  white-space: nowrap;
  color: white;
  transition: 0.2s;
  border: none;
  width: ${({ block }) => block && '100%'};
  border-radius: var(--buttonBorderRadius);
  font-family: var(--defaultFontFamily);
  position: relative;
  overflow: hidden;

  &:active {
    transform: scale(0.97);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &:hover {
    cursor: var(--hoverCursorURL), pointer;
  }

  ${({ size }) => buttonSizeMap[size]};
  ${({ shape, color }) =>
    shape && color
      ? buttonShapeMap[shape][color]
      : buttonShapeMap['filled']['default']}
`;
